import React from 'react';
import {
	Slide,
	type SlideProps,
	Snackbar as MuiSnackbar,
	type SnackbarProps as MuiSnackbarProps,
} from '@mui/material';
import {Alert, type AlertProps} from '@netx/core-components/Alert';
import {useConstCallback} from '@netx/core-components/hooks/useEvent';

type _MuiSnackbarProps = Required<MuiSnackbarProps>;

// handle snackbar transition into view
const TransitionUp = React.forwardRef<HTMLElement, SlideProps>((props, ref) => {
	return <Slide direction="up" ref={ref} {...props} />;
}) as (props: SlideProps) => JSX.Element;

export interface SnackbarProps
	extends MuiSnackbarProps,
		Pick<AlertProps, 'severity' | 'variant'> {
	preventCloseOnClickAway?: boolean;
}

export const SnackbarPrimitive = ({
	message,
	preventCloseOnClickAway,
	severity = 'info',
	variant = 'filled',
	onClose,
	...props
}: SnackbarProps) => {
	const handleClose = useConstCallback<_MuiSnackbarProps['onClose']>(
		(event, reason) => {
			if (!preventCloseOnClickAway || reason !== 'clickaway') {
				onClose?.(event, reason);
			}
		},
	);

	return (
		<MuiSnackbar
			anchorOrigin={{
				horizontal: 'center',
				vertical: 'bottom',
			}}
			autoHideDuration={6000}
			TransitionComponent={TransitionUp}
			{...props}
			onClose={handleClose}
			style={{
				cursor: props.onClick ? 'pointer' : undefined,
				...props.style,
			}}
		>
			<Alert
				onClose={(event) => {
					event.stopPropagation();
					handleClose(event, 'escapeKeyDown');
				}}
				severity={severity}
				variant={variant}
			>
				{message}
			</Alert>
		</MuiSnackbar>
	);
};

export const Snackbar = SnackbarPrimitive;

export default Snackbar;
